import { PagedResponse } from 'app/shared/models/paged-response.model';
import { Service, inject } from '@angular/core';
import { CheckInStatusEnum } from 'app/shared/enums/check-in-status-type.enum';
import { LocationTypeEnum } from 'app/shared/enums/location-type.enum';
import { CustomerCategoryDTO } from 'app/shared/models/customer-category.model';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { BasicHttpClientService } from 'app/shared/services/basic-http-client.service';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PointOfSaleDto } from 'app/shared/models/point-of-sale.model';
import { LocationMapper } from '../../utilities/location-mapper';

import { LocationBaseDTO, LocationFullDTO } from '../../../../shared/models/location.model';
import { CustomerTypeEnum } from '../../enum/customer-type-enum.enum.model';
import {
  CompanyFormDTO,
  CustomerCheckinVM,
  CustomerDiscountDTO,
  ICompanyForm,
  PaymentTermDto,
} from '../../models/back-office-admin-common/customer-check-in.model';
import { LocationCustomerDTO } from '../../models/customer/customer-location.model';
import { BlockOrderTypeEnum, CompanyReportDTO, CustomerFullDTO, CustomerFullViewModel, CustomerVM, GeneralFieldsDTO, GeneralFieldsTypeEnum } from '../../models/customer/customer.model';
import { PointOfSaleDetailVM } from '../../models/point-of-sale/bo-point-of-sale.model';
import { ChangesHandler } from '../../models/salesNetwork/changes-handler.model';
import { CustomerMapper } from '../../utilities/customer-mapper';
import { PaymentTermOverrideMapper } from '../../utilities/payment-term-override-mapper';
import { SessionStore } from 'app/shared/state/session.store';
import { ShopContextStore } from 'app/shared/state/shop-context.store';
import { FormGroup } from '@angular/forms';
import { RegisterCustomerVM } from '../../models/customer/customer-registration.model';
import { CountriesHanler, CountryDTO } from 'app/shared/utilities/countries.models';
import { TenantTypeEnum } from 'environments/tenant-type.enum';
import { CountriesService } from 'app/shared/services/countries.service';
import { OnlineStoreVM, StoreCustomerLocationGetDto } from '../../models/point-of-sale/storePointOfSale.model';
import { CreateOrEditUserDTO } from '../../models/users/user.model';

export interface GetAllCustomersParams {
  searchString?: string,
  skipCount?: number,
  maxResultCount?: number,
  agencyId?: number,
  sorting?: string,
  includePointOfSale?: boolean,
  includeLocation?: boolean,
  customerType?: CustomerTypeEnum,
  checkInStatus?: CheckInStatusEnum,
  storeId?: number,
  associableToPriceListId?: number,
  voucherId?: number,
  includeCustomerVouchers?: boolean,
  externalId?: string,
  insertCompanyFormInName?: boolean,
  blockOrderType?: BlockOrderTypeEnum | BlockOrderTypeEnum[],
  isInvoicingAccount?: boolean,
  isCentralPaymentAccount?: boolean,
  customerGroupCodeId?: number,
  customerGroupId?: number,
  customerHoldingId?: number,
  customerSignboardId?: number,
  customerSubGroupCodeId?: number,
  salesTaxGroupId?: number
}
@Service()
export class CustomerService {
  private _httpClient = inject(BasicHttpClientService);
  private _countriesService = inject(CountriesService);
  private baseUrl: string;

  private _updatedCustomerCheckin = new Subject<boolean>();
  get updatedCustomerCheckin(): Observable<boolean> { return this._updatedCustomerCheckin.asObservable(); }
  updatedCheckin() { this._updatedCustomerCheckin.next(true); }

  private _currentCustomerDiscounts = new BehaviorSubject<CustomerDiscountDTO[]>(null);
  get currentCustomerDiscounts(): Observable<CustomerDiscountDTO[]> { return this._currentCustomerDiscounts.asObservable(); }

  private saveAndSendErpSubject = new BehaviorSubject<boolean>(false);
  saveAndSendErp$ = this.saveAndSendErpSubject.asObservable();

  private _updateCustomerGrid = new Subject<boolean>();
  get updateCustomerGrid(): Observable<boolean> { return this._updateCustomerGrid.asObservable(); }
  triggerUpdateCustomerGrid(isDestroyed: boolean) { this._updateCustomerGrid.next(isDestroyed); }

  private _session = inject(SessionStore);
  private _shopContext = inject(ShopContextStore);

  constructor() {
    this.baseUrl = environment.urls.apiUrl.url;
  }

  triggerSaveAndSendErp() {
    this.saveAndSendErpSubject.next(true);
  }

  resetTrigger() {
    this.saveAndSendErpSubject.next(false);
  }

  getCompanyFormsList(loaderId: string, skipCount?: number, maxResultCount?: number, searchString?: string, sorting?: string, externalId?: number) {
    const params = UtilityFunctions.buildParams(
      ["skipCount", skipCount],
      ["maxResultCount", maxResultCount],
      ["searchString", searchString],
      ["sorting", sorting],
      ["externalId", externalId])

    return this._httpClient.get<CompanyFormDTO[]>(`${this.baseUrl}/company-forms`, { params }, loaderId);
  }

  generateExternalId(loaderId: string): Observable<{ externalId: string }> {
    return this._httpClient.post<{ externalId: string }>(`${this.baseUrl}/customers/generate-external-id`, null, null, loaderId);
  }

  checkExistingExternalId(loaderId: string, externalId: string): Observable<boolean> {
    return this._httpClient.get<boolean>(`${this.baseUrl}/customers/check-external-id-unique`, { params: { externalId } }, loaderId);
  }

getAllCustomers(
  loaderId: string,
  params: GetAllCustomersParams = {}
): Observable<PagedResponse<CustomerVM>> {

  const queryParams = UtilityFunctions.buildParams(
    ['searchString', params.searchString],
    ['skipCount', params.skipCount],
    ['maxResultCount', params.maxResultCount],
    ['agencyId', params.agencyId],
    ['sorting', params.sorting],
    ['includePointOfSale', params.includePointOfSale],
    ['includeLocation', params.includeLocation],
    ['customerType', params.customerType],
    ['checkInStatus', params.checkInStatus],
    ['storeId', params.storeId],
    ['voucherId', params.voucherId],
    ['includeCustomerVouchers', params.includeCustomerVouchers],
    ['externalId', params.externalId],
    ['insertCompanyFormInName', params.insertCompanyFormInName],
    ['blockOrderType', params.blockOrderType],
    ['isInvoicingAccount', params.isInvoicingAccount],
    ['isCentralPaymentAccount', params.isCentralPaymentAccount],
    ['customerGroupCodeId', params.customerGroupCodeId],
    ['customerGroupId', params.customerGroupId],
    ['customerHoldingId', params.customerHoldingId],
    ['customerSignboardId', params.customerSignboardId],
    ['customerSubGroupCodeId', params.customerSubGroupCodeId],
    ['salesTaxGroupId', params.salesTaxGroupId],
    ['associableToPriceListId', params.associableToPriceListId]
  );

  const subject = new Subject<PagedResponse<CustomerVM>>();

  this._httpClient.get<PagedResponse<CustomerFullDTO>>(
    this.baseUrl + '/customers',
    { params: queryParams },
    loaderId
  ).subscribe(data => {
    const res = new PagedResponse<CustomerVM>();

    res.items = data.items.map(x =>
      CustomerMapper.CustomerVM(
        x,
        this._shopContext.shops(),
        this._shopContext.currentShop().id,
        params.insertCompanyFormInName
      )
    );

    res.totalCount = data.totalCount;

    const customerIds = res.items.map(c => c.id).filter(Boolean);

    if (params.includePointOfSale && customerIds.length) {
      this._httpClient.get<PagedResponse<PointOfSaleDto>>(
        this.baseUrl + '/point-of-sales',
        { params: { customerIds, maxResultCount: 1000, skipCount: 0 } }
      ).subscribe(posData => {
        const shops = this._shopContext.shops();
        const storeId = this._shopContext.currentShop().id;
        const posDetailsByCustomer = new Map<number, PointOfSaleDetailVM[]>();

        posData.items.forEach(pos => {
          const detail = LocationMapper.PointOfSaleDetail(
            pos,
            [],
            shops,
            storeId
          );

          const list = posDetailsByCustomer.get(pos.customerId) || [];
          list.push(detail);
          posDetailsByCustomer.set(pos.customerId, list);
        });

        res.items.forEach(customer => {
          customer.pointsOfSale =
            posDetailsByCustomer.get(customer.id) || [];
        });

        subject.next(res);
      });
    } else {
      subject.next(res);
    }
  });

  return subject.asObservable();
}

  getCustomer(id: number, loaderId?: string): Observable<CustomerFullDTO> {
    return this._httpClient.get<CustomerFullDTO>(`${this.baseUrl}/customers/${id}`, null, loaderId).pipe(
      map(customer => {
        customer.acceptsOrders = customer.blockOrderType === BlockOrderTypeEnum.Open;
        return customer;
      }),
      switchMap(customer => {
        const billingLocationId = customer?.customerLocations
          ?.find(x => x.locationType === LocationTypeEnum.BillingLocation)
          ?.location?.id;

        if (!billingLocationId) {
          return of(customer); // non serve fare altre chiamate
        }

        // Se esiste la billing location, la recuperiamo
        return this._httpClient.get<LocationFullDTO>(`${this.baseUrl}/locations/${billingLocationId}`, null, loaderId).pipe(
          map(location => {
            customer.billingLocationStoreCustomerLocations =
              location.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation)
                ?.storeCustomerLocations;
            return customer;
          })
        );
      })
    );
  }

  advancedCustomerRegister(advancedRegister: RegisterCustomerVM, loaderId: string): Observable<CustomerFullDTO> {
    const body = new RegisterCustomerVM({
      billingLocation: new LocationBaseDTO({
        address1: advancedRegister.billingLocation.address1,
        address2: advancedRegister.billingLocation.address2,
        city: advancedRegister.billingLocation.city,
        country: advancedRegister.billingLocation.country,
        countryCode: advancedRegister.billingLocation.countryCode,
        countryId: advancedRegister.billingLocation.countryId,
        externalId: advancedRegister.billingLocation.externalId,
        id: advancedRegister.billingLocation.id,
        latitude: advancedRegister.billingLocation.latitude,
        longitude: advancedRegister.billingLocation.longitude,
        placeId: advancedRegister.billingLocation.placeId,
        province: advancedRegister.billingLocation.province,
        zipCode: advancedRegister.billingLocation.zipCode,
      }),
      billingLocationContacts: advancedRegister.billingLocationContacts,
      currentStoreId: advancedRegister.currentStoreId,
      customer: new CustomerFullDTO({
        abi: advancedRegister.customer.abi,
        availableAllowance: advancedRegister.customer.availableAllowance,
        bankAccountId: advancedRegister.customer.bankAccountId,
        bankAccountNumber: advancedRegister.customer.bankAccountNumber,
        bankName: advancedRegister.customer.bankName,
        basicCardId: advancedRegister.customer.basicCardId,
        cab: advancedRegister.customer.cab,
        centralPaymentNumber: advancedRegister.customer.centralPaymentNumber,
        centralPaymentParentCustomerId: advancedRegister.customer.centralPaymentParentCustomerId,
        checkInMessage: advancedRegister.customer.checkInMessage,
        checkInStatus: advancedRegister.customer.checkInStatus,
        commercialAllowance: advancedRegister.customer.commercialAllowance,
        companyFormId: advancedRegister.customer.companyFormId,
        creditLimit: advancedRegister.customer.creditLimit,
        currencyType: advancedRegister.customer.currencyType,
        customerType: advancedRegister.customer.customerType,
        externalId: advancedRegister.customer.externalId,
        fiscalCode: advancedRegister.customer.fiscalCode,
        glnNumber: advancedRegister.customer.glnNumber,
        iban: advancedRegister.customer.iban,
        invoicingParentCustomerId: advancedRegister.customer.invoicingParentCustomerId,
        isActiveEnterprise: advancedRegister.customer.isActiveEnterprise,
        isCentralPaymentAccount: advancedRegister.customer.isCentralPaymentAccount,
        isCompliantActivity: advancedRegister.customer.isCompliantActivity,
        isCorrectRegistry: advancedRegister.customer.isCorrectRegistry,
        isFactor: advancedRegister.customer.isFactor,
        isInvoicingAccount: advancedRegister.customer.isInvoicingAccount,
        knownAs: advancedRegister.customer.knownAs,
        name: advancedRegister.customer.name,
        notifyStoreId: advancedRegister.customer.notifyStoreId,
        packageGroupModeType: advancedRegister.customer.packageGroupModeType,
        paCode: advancedRegister.customer.paCode,
        paymentTermId: advancedRegister.customer.paymentTermId,
        pecEmail: advancedRegister.customer.pecEmail,
        routingNumber: advancedRegister.customer.routingNumber,
        sdiCode: advancedRegister.customer.sdiCode,
        sirenNumber: advancedRegister.customer.sirenNumber,
        swift: advancedRegister.customer.swift,
        vatNumber: advancedRegister.customer.vatNumber,
      }),
      customerCategoryIds: advancedRegister.customerCategoryIds,
      existingUserId: advancedRegister.existingUserId,
      financialReport: advancedRegister.financialReport,
      generalFieldIds: advancedRegister.generalFieldIds,
      paymentTermOverrides: advancedRegister.paymentTermOverrides,
      pointsOfSales: advancedRegister.pointsOfSales.map(p => new PointOfSaleDetailVM({
        alternativeGlnNumber: p.alternativeGlnNumber,
        alternativeVat: p.alternativeVat,
        contacts: p.contacts,
        location: {
          address1: p.location.address1,
          address2: p.location.address2,
          city: p.location.city,
          externalId: p.location.externalId,
          latitude: p.location.latitude,
          longitude: p.location.longitude,
          placeId: p.location.placeId,
          province: p.location.province,
          zipCode: p.location.zipCode,
          countryId: p.location.countryId,
        },
        name: p.name,
        onlineStores: p.onlineStores.map(os => new OnlineStoreVM({
          agencyStoreIds: os.agencyStoreIds,
          channelIds: os.channelIds,
          storeId: os.storeId,
          tierId: os.tierId
        })),
        summerStartDay: p.summerStartDay,
        summerStartMonth: p.summerStartMonth,
        summerStockLimit: p.summerStockLimit,
        useCustomerBillingLocation: p.useCustomerBillingLocation,
        useNameInOrderHeader: p.useNameInOrderHeader,
        winterStartDay: p.winterStartDay,
        winterStartMonth: p.winterStartMonth,
        winterStockLimit: p.winterStockLimit,
      })),
      sendToErp: advancedRegister.sendToErp,
      user: !advancedRegister.user ? null : new CreateOrEditUserDTO({
        externalId: advancedRegister.user.externalId,
        name: advancedRegister.user.name,
        blockInvitation: advancedRegister.user.blockInvitation,
        email: advancedRegister.user.email,
        tenantId: advancedRegister.user.tenantId,
      })
    });
    return this._httpClient.post<CustomerFullDTO>(this.baseUrl + `/customers/creation-advanced`, body, null, loaderId);
  }

  updateCustomer(customer: CustomerFullDTO, loaderId?: string): Observable<CustomerFullDTO> {
    const params = {}
    if (customer.abi) params['abi'] = customer.abi;
    if (customer.acceptsOrders) params['acceptsOrders'] = customer.acceptsOrders;
    if (customer.availableAllowance) params['availableAllowance'] = customer.availableAllowance;
    if (customer.basicCardId) params['basicCardId'] = customer.basicCardId;
    if (customer.cab) params['cab'] = customer.cab;
    if (customer.blockOrderType) params['blockOrderType'] = customer.blockOrderType;
    if (customer.checkInMessage) params['checkInMessage'] = customer.checkInMessage;
    if (customer.checkInStatus) params['checkInStatus'] = customer.checkInStatus;
    if (customer.commercialAllowance) params['commercialAllowance'] = customer.commercialAllowance;
    if (customer.companyFormId) params['companyFormId'] = customer.companyFormId;
    if (customer.customerType) params['customerType'] = customer.customerType;
    if (customer.externalId) params['externalId'] = customer.externalId;
    if (customer.fiscalCode) params['fiscalCode'] = customer.fiscalCode;
    // if (customer.gokEmail) params['gokEmail'] = customer.gokEmail;
    // if (customer.gokNumber) params['gokNumber'] = customer.gokNumber;
    if (customer.isActiveEnterprise) params['isActiveEnterprise'] = customer.isActiveEnterprise;
    if (customer.isCompliantActivity) params['isCompliantActivity'] = customer.isCompliantActivity;
    if (customer.isCorrectRegistry) params['isCorrectRegistry'] = customer.isCorrectRegistry;
    if (customer.name) params['name'] = customer.name;
    if (customer.knownAs) params['knownAs'] = customer.knownAs;
    if (customer.packageGroupModeType) params['packageGroupModeType'] = customer.packageGroupModeType;
    if (customer.paCode) params['paCode'] = customer.paCode;
    if (customer.paymentTermId) params['paymentTermId'] = customer.paymentTermId;
    if (customer.pecEmail) params['pecEmail'] = customer.pecEmail;
    if (customer.sdiCode) params['sdiCode'] = customer.sdiCode;
    if (customer.swift) params['swift'] = customer.swift;
    if (customer.vatNumber) params['vatNumber'] = customer.vatNumber;
    if (customer.sendToErp) params['sendToErp'] = customer.sendToErp;

    if (customer.bankAccountId) params['bankAccountId'] = customer.bankAccountId;
    if (customer.bankAccountNumber) params['bankAccountNumber'] = customer.bankAccountNumber;
    if (customer.bankName) params['bankName'] = customer.bankName;
    if (customer.centralPaymentNumber) params['centralPaymentNumber'] = customer.centralPaymentNumber;
    if (customer.centralPaymentParentCustomerId) params['centralPaymentParentCustomerId'] = customer.centralPaymentParentCustomerId;
    if (customer.creditLimit) params['creditLimit'] = customer.creditLimit;
    if (customer.routingNumber) params['routingNumber'] = customer.routingNumber;
    if (customer.currencyType) params['currencyType'] = customer.currencyType;
    if (customer.glnNumber) params['glnNumber'] = customer.glnNumber;
    if (customer.iban) params['iban'] = customer.iban;
    if (customer.invoicingParentCustomerId) params['invoicingParentCustomerId'] = customer.invoicingParentCustomerId;
    if (customer.isCentralPaymentAccount) params['isCentralPaymentAccount'] = customer.isCentralPaymentAccount;
    if (customer.isFactor) params['isFactor'] = customer.isFactor;
    if (customer.isInvoicingAccount) params['isInvoicingAccount'] = customer.isInvoicingAccount;
    if (customer.sirenNumber) params['sirenNumber'] = customer.sirenNumber;

    params['sendToErp'] = customer.sendToErp ?? false;
    // return this._httpClient.put<CustomerFullDTO>(this.baseUrl + `/customers/${customer.id}`, params, null, loaderId);
    let requests: HttpBasicRequest[] = [{ method: 'put', url: this.baseUrl + `/customers/${customer.id}`, body: params }];
    if (!customer?.billingLocationStoreCustomerLocations?.length && !!customer.customerLocations?.find(x => x.locationType === LocationTypeEnum.BillingLocation)?.location?.id) {
      requests.push(new HttpBasicRequest({
        method: 'post', url: this.baseUrl + `/customers/${customer.id}/store-location/${customer.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation).location.id}`, body: { storeId: this._shopContext.currentShop()?.id, checkInStatus: CheckInStatusEnum.ToApprove }
      }));
    }
    return this._httpClient.forkJoin(requests, loaderId).pipe(map(data => {
      return data[0] as CustomerFullDTO;
    }))
  }

  getCustomerAndGeneralFields(customerId: number, loaderId?: string): Observable<any> {
    const requests: HttpBasicRequest[] = [
      { url: this.baseUrl + `/customers/${customerId}`, body: null, method: 'get' },
      { url: this.baseUrl + `/customers/${customerId}/general-fields`, body: null, method: 'get' },
    ];

    return this._httpClient.forkJoin<any>(requests, loaderId).pipe(
      switchMap(([customer, generalFields]) => {
        const billingLocationId = customer.customerLocations
          ?.find(x => x.locationType === LocationTypeEnum.BillingLocation)
          ?.location?.id;

        if (!billingLocationId) {
          return of({ customer, generalFields });
        }

        return this._httpClient.get<LocationFullDTO>(`${this.baseUrl}/locations/${billingLocationId}`, null, loaderId).pipe(
          map(location => {
            customer.billingLocationStoreCustomerLocations =
              location.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation)
                ?.storeCustomerLocations;

            return { customer, generalFields };
          })
        );
      })
    );
  }

  updateCustomerAndInternalObjectsCustomerInfo(loaderId: string, updatedCustomer: CustomerFullDTO, oldCustomer: CustomerFullDTO, sendToErp: boolean, customerForm?: FormGroup): Observable<{ locationBody: any, updatedCustomerResponse: CustomerFullDTO }> {
    updatedCustomer.sendToErp = sendToErp;

    const customerRequests: HttpBasicRequest[] = [
      {
        url: `${this.baseUrl}/customers/${oldCustomer.id}`,
        body: updatedCustomer,
        method: 'put'
      }
    ];

    let locationBody: LocationBaseDTO = {};

    const executeRequests = () => {
      if (!updatedCustomer?.billingLocationStoreCustomerLocations?.length && !!updatedCustomer.customerLocations?.find(x => x.locationType === LocationTypeEnum.BillingLocation)?.location?.id) {
        customerRequests.push(new HttpBasicRequest({
          method: 'post',
          url: this.baseUrl + `/customers/${updatedCustomer.id}/store-location/${updatedCustomer.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation).location.id}`,
          body: { storeId: this._shopContext.currentShop()?.id, checkInStatus: CheckInStatusEnum.ToApprove }
        }));
      }

      return this._httpClient.forkJoin<any>(customerRequests, loaderId).pipe(
        map((responses) => {
          const customerResponse = responses[0] as CustomerFullDTO;
          customerResponse.billingLocationStoreCustomerLocations = updatedCustomer?.billingLocationStoreCustomerLocations || [new StoreCustomerLocationGetDto({
            checkInMessage: null,
            checkInStatus: CheckInStatusEnum.ToApprove,
            store: this._shopContext.currentShop()
          })];
          return {
            locationBody: locationBody,
            updatedCustomerResponse: customerResponse
          };
        })
      );
    };

    if (customerForm) {
      locationBody = {
        address1: customerForm.get('address1')?.value,
        address2: customerForm.get('address2')?.value,
        province: customerForm.get('province')?.value,
        zipCode: customerForm.get('zipCode')?.value,
        city: customerForm.get('city')?.value,
        country: customerForm.get('country')?.value,
        countryId: customerForm.get('countryId')?.value
      };

      if (oldCustomer.customerLocations?.[0]?.location?.id) {
        if (locationBody.address1?.trim()) {
          customerRequests.push({
            url: `${this.baseUrl}/locations/${oldCustomer.customerLocations[0].location.id}`,
            body: locationBody,
            method: 'put'
          });
        }
        return executeRequests();
      } else {
        if (locationBody.address1?.trim()) {
          return this._httpClient.post<LocationFullDTO>(`${this.baseUrl}/locations`, locationBody).pipe(
            switchMap(createLocationResponse => {
              customerRequests.push({
                url: `${this.baseUrl}/customers/${oldCustomer.id}/location`,
                body: {
                  locationId: createLocationResponse.id,
                  locationType: 1
                },
                method: 'post'
              });
              locationBody.id = createLocationResponse.id;
              return executeRequests();
            })
          );
        }
        return executeRequests();
      }
    }
    return executeRequests();
  }

  getCheckInData(loaderId: string, customer: CustomerFullDTO): Observable<CustomerCheckinVM> {
    const checkIn = new Subject<CustomerCheckinVM>();
    //let customerCheckin: CustomerCheckinVM;
    const params = { skipCount: 0, maxResultCount: 0, sorting: 'name' };
    //const customerMissingFields = new Subject<Map<string, string>>();
    const customerRequests: HttpBasicRequest[] = [
      { url: this.baseUrl + `/customers/${customer.id}/locations?LocationType=${LocationTypeEnum.BillingLocation}`, method: 'get' },
      { url: this.baseUrl + `/customer-categories`, method: 'get', options: { params: { skipCount: 0, maxResultCount: 0 } } },
      { url: this.baseUrl + `/company-forms`, method: 'get', options: { params } },
      { url: this.baseUrl + `/payment-terms`, method: 'get', options: { params: { skipCount: 0, maxResultCount: 0, sorting: 'name' } } },
      { url: this.baseUrl + `/customer-discount`, method: 'get', options: { params: { customerId: customer.id, maxResultCount: 0, skipCount: 0, onlyValidNow: true } } }
    ];
    const locationId = customer.customerLocations.find(x => x.locationType === LocationTypeEnum.BillingLocation)?.location?.id;
    if (!!locationId) customerRequests.push({ url: this.baseUrl + `/locations/${locationId}`, method: 'get' });

    this._httpClient.forkJoin<any>(customerRequests, loaderId).subscribe(res => {
      const locationsDTO: PagedResponse<LocationCustomerDTO> = res[0];
      const categoriesDTO: PagedResponse<CustomerCategoryDTO> = res[1];
      const formsDTO: PagedResponse<ICompanyForm> = res[2];
      const listPaymentTerms: PagedResponse<PaymentTermDto> = res[3];
      const customerDiscounts: PagedResponse<CustomerDiscountDTO> = res[4];
      const billingFromCusLocations = locationsDTO.items?.find(x => x.locationType === LocationTypeEnum.BillingLocation);
      const billingLocation = res[5] as LocationFullDTO;
      if (!!billingLocation && !!billingFromCusLocations && billingFromCusLocations.location.id === billingLocation.id)
        billingFromCusLocations.location.customerLocations = billingLocation.customerLocations;
      this._currentCustomerDiscounts.next(customerDiscounts.items);
      const customerCheckin = CustomerMapper.CustomerCheckin(customer, categoriesDTO.items, billingFromCusLocations, formsDTO.items, listPaymentTerms?.items);
      checkIn.next(customerCheckin);
    });
    return checkIn.asObservable();
  }


  getRegistrationData(loaderId: string): Observable<any> {
    const returnData = new Subject();
    const params = { skipCount: 0, maxResultCount: 0, sorting: 'name' };

    this._httpClient.forkJoin<any>([
      { url: this.baseUrl + `/customer-categories`, method: 'get', options: { params: { skipCount: 0, maxResultCount: 0 } } },
      { url: this.baseUrl + `/company-forms`, method: 'get', options: { params } },
      { url: this.baseUrl + `/payment-terms`, method: 'get', options: { params: { skipCount: 0, maxResultCount: 0, sorting: 'name' } } }
    ], loaderId)
      .subscribe(res => {
        res.push(CustomerMapper.GetListVatPrefix());
        returnData.next(res);
      });
    return returnData.asObservable();
  }

  associateCutomerContact(customerId: number, locationId: number, contactId: number, loaderId: string) {
    return this._httpClient.post(`${this.baseUrl}/customers/${customerId}/contact-loc/${locationId}`, { contactId }, null, loaderId)
  }

  disassociateCutomerContact(customerId: number, locationId: number, contactId: number, loaderId: string) {
    const params = UtilityFunctions.buildParams(
      ['locationId', locationId],
      ['contactId', contactId],
    )
    return this._httpClient.delete(`${this.baseUrl}/customers/${customerId}/contact`, { params }, loaderId)
  }

  savePaymentTermsOverride(checkInData: CustomerCheckinVM, cacheCheckInData: CustomerCheckinVM, loaderId: string, checkInStatus: CheckInStatusEnum = CheckInStatusEnum.ToApprove) {
    const result = new Subject();
    this._httpClient.forkJoin(
      PaymentTermOverrideMapper.PaymentTermOverrideRequest(checkInData.paymentInfo.paymentTermOverrides,
        cacheCheckInData.paymentInfo.paymentTermOverrides,
        this.baseUrl,
        checkInData.companyInfo.id
      ), loaderId).subscribe((res) => {
        result.next(res ? res[0] as CustomerFullDTO : null);
        this.updatedCheckin();
      }, error => {
        console.error(error);
      });
    return result.asObservable();
  }

  registerCustomer(checkInData: CustomerCheckinVM, posDataArray: PointOfSaleDetailVM[], loaderId: string) {
    const form = checkInData.companyInfo.listCompanyForm?.find(cf => cf.id === checkInData.companyInfo.companyFormId);
    checkInData.companyInfo.name = UtilityFunctions.manipulateCompanyName(checkInData.companyInfo.name, form ? [form] : []).manipulatedName;
    const result = new Subject();
    const notUsePrefixTenantCongig = environment?.tenants?.find(x => x.id?.toLowerCase() === this._session.userInfo()?.tenant?.id?.toLowerCase())?.notUsePrefixForLocalVat;
    const notUsePrefix: boolean = !!notUsePrefixTenantCongig?.value && checkInData.companyInfo.vatPrefix === notUsePrefixTenantCongig?.vatPrefix;
    const removeSpaceFromVat = environment?.tenants?.find(x => x.id?.toLowerCase() === this._session.userInfo()?.tenant?.id?.toLowerCase())?.removeSpaceFromVat;
    const customerRequest = CustomerMapper.RegisterCustomerCreateRequest(checkInData, posDataArray, notUsePrefix, removeSpaceFromVat);
    this._httpClient.post(this.baseUrl + '/customers/register', customerRequest, { headers: { 'Content-Type': 'application/json' } }, loaderId)
      .subscribe(() => result.next(true));
    return result;
  }

  getCompanyByVatCreditSafe(countryCode: string, vatNumber: string, loaderId: string) {
    const result = new Subject();
    const authBody = {
      username: 'ammendola@basic.net',
      password: 'CO0YNXLW7NQQbHP7;eoOsU'
    };
    this._httpClient.post(`https://connect.creditsafe.com/v1/authenticate`, authBody, { headers: { 'Content-Type': 'application/json' } }, loaderId).subscribe((auth) => {
      this._httpClient.get(`https://connect.creditsafe.com/v1/companies?countries=${(countryCode || 'IT')}&vatNo=${vatNumber}&status=active&officeType=headOffice`,
        { headers: { 'Authorization': auth.token } }, loaderId).subscribe((company) => {
          result.next(company);
        });
    });
    return result.asObservable();
  }

  getCompanyReports(companyId: number, loaderId: string): Observable<CompanyReportDTO[]> {
    const result = new Subject<CompanyReportDTO[]>();
    this._httpClient.get<CompanyReportDTO[]>(this.baseUrl + '/customers/' + companyId + '/customer-summaries', {}, loaderId).subscribe(res => {
      result.next(res);
    }, err => console.error(err));
    return result.asObservable();
  }

  getCompanyReportByVat(countryCode: string, vatNumber: string, loaderId: string): Observable<CompanyReportDTO> {
    const result = new Subject<CompanyReportDTO>();
    const params = { vatNumber, countryCode };
    this._httpClient.get<CompanyReportDTO>(this.baseUrl + '/customers/customer-report-by-vat-number', {
      params
    }, loaderId).subscribe(res => result.next(res), err => console.error(err));
    return result.asObservable();
  }

  requestNewCustomerReport(loaderId: string, customerId: number): Observable<CompanyReportDTO> {
    return this._httpClient.post(this.baseUrl + '/customers/' + customerId + '/customer-report', null, null, loaderId);
  }

  deleteCustomer(loaderId: string, customerId: number): Observable<any> {
    return this._httpClient.delete(this.baseUrl + `/customers/${customerId}`, null, loaderId);
  }

  getCustomerGeneralFields(loaderId: string, customerId: number, type?: GeneralFieldsTypeEnum): Observable<GeneralFieldsDTO[]> {
    const params = {};
    if (type) params['type'] = type;
    return this._httpClient.get(this.baseUrl + `/customers/${customerId}/general-fields`, params, loaderId)
  }

  // getCustomerGeneralFieldsShared(loaderId: string, customerId: number): Observable<GeneralFieldsDTO[]> {
  //   if (!this.generalFieldsCache$[customerId]) {
  //     this.generalFieldsCache$[customerId] = this.getCustomerGeneralFields(loaderId, customerId).pipe(
  //       shareReplay(1)
  //     );
  //   }
  //   return this.generalFieldsCache$[customerId];
  // }

  // clearCustomerGeneralFieldsCache(customerId: number): void {
  //   delete this.generalFieldsCache$[customerId];
  // }

  associateCustomerGeneralField(loaderId: string, customerId: number, generalFieldId: number): Observable<GeneralFieldsDTO> {
    return this._httpClient.post(this.baseUrl + `/customers/${customerId}/general-fields/${generalFieldId}`, null, null, loaderId)
  }

  updateCustomerGeneralFields(loaderId: string, customerId: number, generalFieldId: number): Observable<GeneralFieldsDTO> {
    return this._httpClient.put(this.baseUrl + `/customers/${customerId}/general-fields/${generalFieldId}`, null, null, loaderId)
  }

  // Endpoint dedicato: la PUT completa del cliente (e il sync ERP) riporterebbe il flag a false
  updateOrderConfirmationEmail(customerId: number, blockOrderConfirmationEmailByDefault: boolean, loaderId?: string): Observable<void> {
    return this._httpClient.put(this.baseUrl + `/customers/${customerId}/order-confirmation-email`,
      { blockOrderConfirmationEmailByDefault }, null, loaderId);
  }

  bulkCustomerGeneralFields(loaderId: string, customerId: number, generalFieldIdsToAssociate?: number[], generalFieldIdsToDissociate?: number[]): Observable<GeneralFieldsDTO> {
    const params = {};
    if (generalFieldIdsToDissociate?.length) params['generalFieldIdsToDissociate'] = generalFieldIdsToDissociate;
    if (generalFieldIdsToAssociate?.length) params['generalFieldIdsToAssociate'] = generalFieldIdsToAssociate;
    return this._httpClient.post(this.baseUrl + `/customers/${customerId}/general-fields/bulk`, params, null, loaderId)
  }

  saveCustomerDiscount(loaderId: string, discount: ChangesHandler<CustomerDiscountDTO[]>): Observable<CustomerDiscountDTO[]> {
    const _sub = new Subject<CustomerDiscountDTO[]>();
    const customerDiscountsReqs = CustomerMapper.customerDiscountsRequestHandler(discount.current, discount.cached, this.baseUrl);
    const requests = !!customerDiscountsReqs.putReqs?.length ? customerDiscountsReqs.putReqs : customerDiscountsReqs.postAndDelreqs;
    if (requests?.length) {
      this._httpClient.forkJoin(requests, loaderId).subscribe(() => {
        !!customerDiscountsReqs.putReqs?.length && !!customerDiscountsReqs.postAndDelreqs?.length ?
          this._httpClient.forkJoin(customerDiscountsReqs.postAndDelreqs, loaderId).subscribe(() => _sub.next(discount.current)) :
          _sub.next(discount.current);
      });
    }
    else _sub.next(discount.current);
    return _sub.asObservable();
  }

  getCustomerDiscounts(loaderId: string, customerId: number) {
    const url = this.baseUrl + `/customer-discount`;
    this._httpClient.get<PagedResponse<CustomerDiscountDTO>>(url, { params: { customerId, maxResultCount: 0, skipCount: 0, onlyValidNow: true } }, loaderId)
      .subscribe(data => this._currentCustomerDiscounts.next(data.items));
  }

  customersCheckUniqueness(vatNumber: string, ssn?: string, loaderId?: string): Observable<boolean> {
    const url = this.baseUrl + `/customers/check-uniqueness`;
    const params = { vatNumber };
    if (ssn) params['fiscalCode'] = ssn;
    return this._httpClient.post<boolean>(url, null, { params }, loaderId);
  }

  getCustomerBillingLocations(loaderId: string, customerId: number) {
    return this._httpClient.get<PagedResponse<LocationCustomerDTO>>(this.baseUrl + `/customers/${customerId}/locations?LocationType=${LocationTypeEnum.BillingLocation}`, {}, loaderId);
  }

  deletePosAndCustomer(customer: CustomerVM, posIds: number[], loaderId: string): Observable<any> {
    const _sub = new Subject();
    let reqs: HttpBasicRequest[] = posIds.map(x => new HttpBasicRequest({ method: 'delete', url: this.baseUrl + '/point-of-sales/' + x }));
    if (!!customer.customerLocations?.length) reqs = reqs.concat(customer.customerLocations.map(l => new HttpBasicRequest({ method: 'delete', url: this.baseUrl + '/customers/' + customer.id + '/location/' + l.id })))
    reqs?.length ?
      this._httpClient.forkJoin(reqs, loaderId)
        .subscribe(() => this._httpClient.delete(this.baseUrl + '/customers/' + customer.id, null, loaderId).subscribe(() => _sub.next(true))) :
      this._httpClient.delete(this.baseUrl + '/customers/' + customer.id, null, loaderId).subscribe(() => _sub.next(true));
    return _sub.asObservable();
  }

  getCustomerLocations(loaderId: string, customerId: number, externalId?: string, locationType?: number) {
    const params = {};
    if (locationType) params['locationType'] = locationType;
    if (externalId) params['externalId'] = externalId;

    return this._httpClient.get<PagedResponse<LocationCustomerDTO>>(this.baseUrl + `/customers/${customerId}/locations`, { params }, loaderId);
  }

  getCustomerFull(
    customerId: number,
    loaderId?: string
  ): Observable<CustomerFullViewModel> {

    const requests: HttpBasicRequest[] = [
      { url: `${this.baseUrl}/customers/${customerId}`, body: null, method: 'get' },
      { url: `${this.baseUrl}/customers/${customerId}/general-fields`, body: null, method: 'get' },
      { url: `${this.baseUrl}/customers/${customerId}/locations`, body: null, method: 'get' }
    ];

    return this._httpClient
      .forkJoin(requests, loaderId)
      .pipe(
        switchMap((responses: any[]) => {

          // 🔹 Cast manuale perché il forkJoin custom non è tipizzato
          const customerResponse = responses[0] as CustomerFullDTO;
          const generalFields = responses[1] as GeneralFieldsDTO[];
          const locationsResponse = responses[2] as PagedResponse<LocationCustomerDTO>;

          // 🔹 Creo istanza tipizzata
          const customer = new CustomerFullDTO(customerResponse);

          // 🔹 Setto le locations
          customer.customerLocations = locationsResponse?.items ?? [];

          const billingLocationId = customer.customerLocations
            ?.find(x => x.locationType === LocationTypeEnum.BillingLocation)
            ?.location?.id;

          // 🔹 Se non esiste billing location → ritorno subito
          if (!billingLocationId) {
            return of({
              customer,
              generalFields
            });
          }

          // 🔹 Se esiste → arricchisco con storeCustomerLocations
          return this._httpClient
            .get<LocationFullDTO>(
              `${this.baseUrl}/locations/${billingLocationId}`,
              null,
              loaderId
            )
            .pipe(
              map(location => {

                const billingCustomerLocation = location.customerLocations
                  ?.find(x => x.locationType === LocationTypeEnum.BillingLocation);

                customer.billingLocationStoreCustomerLocations =
                  billingCustomerLocation?.storeCustomerLocations ?? [];

                return {
                  customer,
                  generalFields
                };
              })
            );
        })
      );
  }
}
